import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import { z } from 'zod'
import { createApiClient } from '../api/helpers.ts'
import { env } from '../env.ts'
import { asQueryParam, type Impersonation, useImpersonation } from './impersonation.ts'
import { useSubscription } from './service.ts'

const AssetsApi = createApiClient({ prefixUrl: env.API_ASSETS })

export const assetQuery = (impersonation: Impersonation | undefined, options: { enabled?: boolean } = {}) => {
	return queryOptions({
		queryKey: ['ASSETS', impersonation?.subscriptionId],
		staleTime: 1000 * 60,
		refetchInterval: 1000 * 60,
		enabled: options.enabled,
		queryFn: () =>
			AssetsApi.get('api/v1/site/asset', {
				searchParams: { site: 'true', size: '10000', ...asQueryParam(impersonation) },
			})
				.json<Asset[]>()
				.catch<Asset[]>(() => []),
	})
}
export const useAssets = () => {
	const impersonation = useImpersonation()
	const { data: subscription } = useSubscription()
	return useQuery(assetQuery(impersonation, { enabled: !!subscription?.features.includes('protections') }))
}

export const siteQuery = (impersonation: Impersonation | undefined, options: { enabled?: boolean } = {}) => {
	return queryOptions({
		queryKey: ['SITES', impersonation?.subscriptionId],
		staleTime: 1000 * 60,
		refetchInterval: 1000 * 60,
		enabled: options.enabled,
		queryFn: async () => {
			const pageSize = 10_000
			const maxPages = 200
			let hasMorePages = false
			let page = 0
			const sites: Site[] = []
			do {
				const response = await AssetsApi.get('api/v1/site', { searchParams: { size: pageSize, page, ...asQueryParam(impersonation) } })
					.json<Site[]>()
					.catch<Site[]>(() => [])
				sites.push(...response)
				hasMorePages = response.length === pageSize
				page += 1
			} while (hasMorePages && page <= maxPages)
			return sites
		},
	})
}

export const useSites = () => {
	const impersonation = useImpersonation()
	const { data: subscription } = useSubscription()
	return useQuery(siteQuery(impersonation, { enabled: !!subscription?.features.includes('protections') }))
}

export const useCreateAsset = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation<Asset, Error, AssetRequest>({
		mutationFn: async (params) => {
			const response = await AssetsApi.post('api/v1/site/asset-site', {
				json: AssetRequestSchema.decode(params),
				searchParams: asQueryParam(impersonation),
				timeout: 29_000,
			}).json<Asset>()
			return response
		},
		onSettled: () =>
			void Promise.all([queryClient.invalidateQueries(assetQuery(impersonation)), queryClient.invalidateQueries(siteQuery(impersonation))]),
		onError: (error) => console.error('Error creating asset', error),
	})
}

export const useDeleteAsset = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation<void, Error, string>({
		mutationFn: async (assetVrn) => {
			await AssetsApi.delete('api/v1/site/asset-site', { searchParams: { assetVrn, ...asQueryParam(impersonation) } }).json<void>()
		},
		onSettled: async () => {
			await Promise.all([
				queryClient.invalidateQueries(assetQuery(impersonation)),
				queryClient.invalidateQueries(siteQuery(impersonation)),
				queryClient.invalidateQueries(favoriteAssetsQuery(impersonation)),
			])
		},
		onError: (error) => console.error('Error deleting asset', error),
	})
}

export const useUpdateAsset = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation<Asset, Error, UpdateAssetRequest>({
		mutationFn: async (params) => {
			const response = await AssetsApi.put('api/v1/site/asset-site', {
				json: UpdateAssetRequestSchema.decode(params),
				searchParams: asQueryParam(impersonation),
				timeout: 29_000,
			}).json<Asset>()
			return response
		},
		onSettled: () =>
			void Promise.all([queryClient.invalidateQueries(assetQuery(impersonation)), queryClient.invalidateQueries(siteQuery(impersonation))]),
		onError: (error) => console.error('Error updating asset', error),
	})
}

const favoriteAssetsQuery = (impersonation: Impersonation | undefined, options: { enabled?: boolean } = {}) => {
	return queryOptions({
		queryKey: ['FAVORITE_ASSETS', impersonation?.subscriptionId],
		staleTime: 1000 * 60,
		refetchInterval: 1000 * 60,
		enabled: options.enabled,
		queryFn: () =>
			AssetsApi.get('api/v1/site/asset-bookmark', {
				searchParams: { ...asQueryParam(impersonation) },
			})
				.json<AssetFavorite[]>()
				.catch<AssetFavorite[]>(() => []),
	})
}

export const useFavoriteAssets = () => {
	const impersonation = useImpersonation()
	const { data: subscription } = useSubscription()
	return useQuery(favoriteAssetsQuery(impersonation, { enabled: !!subscription?.features.includes('protections') }))
}

export const useAddFavoriteAssets = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation<void, Error, { vrns: string[] }, { previous: AssetFavorite[] | undefined }>({
		mutationFn: async (input) => {
			await AssetsApi.post('api/v1/site/asset-bookmark', {
				json: input,
				searchParams: asQueryParam(impersonation),
			}).json<void>()
		},
		onMutate: async ({ vrns }) => {
			const queryKey = favoriteAssetsQuery(impersonation).queryKey
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData<AssetFavorite[]>(queryKey)
			queryClient.setQueryData<AssetFavorite[]>(queryKey, (old) => [
				...(old ?? []),
				...vrns.map((assetVrn) => ({ id: '', assetVrn, ownerVrn: '', userVrn: '' })),
			])
			return { previous }
		},
		onSettled: () => queryClient.invalidateQueries(favoriteAssetsQuery(impersonation)),
		onError: (error, _, context) => {
			console.error('Error creating asset bookmark', error)
			queryClient.setQueryData(favoriteAssetsQuery(impersonation).queryKey, context?.previous)
		},
	})
}

export const useDeleteFavoriteAssets = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation<void, Error, { vrns: string[] }, { previous: AssetFavorite[] | undefined }>({
		mutationFn: async (input) => {
			await AssetsApi.delete('api/v1/site/asset-bookmark', {
				json: input,
				searchParams: asQueryParam(impersonation),
			}).json<void>()
		},
		onMutate: async ({ vrns }) => {
			const queryKey = favoriteAssetsQuery(impersonation).queryKey
			await queryClient.cancelQueries({ queryKey })
			const previous = queryClient.getQueryData<AssetFavorite[]>(queryKey)
			queryClient.setQueryData<AssetFavorite[]>(queryKey, (old) => old?.filter((fav) => !vrns.includes(fav.assetVrn)) ?? [])
			return { previous }
		},
		onSettled: () => queryClient.invalidateQueries(favoriteAssetsQuery(impersonation)),
		onError: (error, _, context) => {
			console.error('Error deleting asset bookmark', error)
			queryClient.setQueryData(favoriteAssetsQuery(impersonation).queryKey, context?.previous)
		},
	})
}

export const useUpdateFavoriteAssets = () => {
	const impersonation = useImpersonation()
	const queryClient = useQueryClient()
	return useMutation<void, Error, { vrns: string[] }>({
		mutationFn: async (input) => {
			await AssetsApi.put('api/v1/site/asset-bookmark', {
				json: input,
				searchParams: asQueryParam(impersonation),
			}).json<void>()
		},
		onSettled: () => queryClient.invalidateQueries(favoriteAssetsQuery(impersonation)),
		onError: (error) => console.error('Error updating asset bookmarks', error),
	})
}

const AssetFavoriteSchema = z.object({
	id: z.string(),
	assetVrn: z.string(),
	ownerVrn: z.string(),
	userVrn: z.string(),
})

type AssetFavorite = z.infer<typeof AssetFavoriteSchema>

export interface Asset {
	id: string
	vrn: string
	siteVrn: string
	site: Site
	name: string
	type: AssetType
	description?: string
	createdTime: string
	removedTime?: string
	metadata: {
		hubHeight?: number
		maxMWCapacity?: number
		powerCurve?: PowerCurve
	}
}

export interface Site {
	id: string
	vrn: string
	ownerVrn: string
	parentVrn?: string
	name: string
	description?: string
	geom: Geometry
	createdTime: string
	removedTime?: string
}

const PointSchema = z.object({ type: z.literal('Point'), coordinates: z.array(z.number()) })
const MultiPointSchema = z.object({ type: z.literal('MultiPoint'), coordinates: z.array(z.array(z.number())) })
const LineStringSchema = z.object({ type: z.literal('LineString'), coordinates: z.array(z.array(z.number())) })
const MultiLineStringSchema = z.object({ type: z.literal('MultiLineString'), coordinates: z.array(z.array(z.array(z.number()))) })
const PolygonSchema = z.object({ type: z.literal('Polygon'), coordinates: z.array(z.array(z.array(z.number()))) })
const MultiPolygonSchema = z.object({ type: z.literal('MultiPolygon'), coordinates: z.array(z.array(z.array(z.array(z.number())))) })
const GeometrySchema = z.union([PointSchema, MultiPointSchema, LineStringSchema, MultiLineStringSchema, PolygonSchema, MultiPolygonSchema])
type Geometry = z.infer<typeof GeometrySchema>

export const AssetType = {
	AIRPORT: 'AIRPORT',
	BUILDING: 'BUILDING',
	CONST_SITE: 'CONST_SITE',
	FARM: 'FARM',
	FIRE: 'FIRE',
	FOREST: 'FOREST',
	HELIPAD: 'HELIPAD',
	LAKE: 'LAKE',
	MANUFACTURING: 'MANUFACTURING',
	MINE: 'MINE',
	NUCLEAR_FACILITY: 'NUCLEAR_FACILITY',
	OTHER: 'OTHER',
	PARK: 'PARK',
	PORT: 'PORT',
	POWER_PLANT: 'POWER_PLANT',
	RIVER: 'RIVER',
	ROAD: 'ROAD',
	ROUTE: 'ROUTE',
	RUNWAY: 'RUNWAY',
	SOLAR_FARM: 'SOLAR_FARM',
	STADIUM: 'STADIUM',
	SUBSTATION: 'SUBSTATION',
	TOWER: 'TOWER',
	TRAIL: 'TRAIL',
	TRANSMISSION_LINE: 'TRANSMISSION_LINE',
	TRANSMISSION_TERMINAL_STATION: 'TRANSMISSION_TERMINAL_STATION',
	WIND_FARM: 'WIND_FARM',
	WIND_TURBINE: 'WIND_TURBINE',
} as const
export type AssetType = (typeof AssetType)[keyof typeof AssetType]

export const PowerCurve = {
	GE_GE15sle: 'GE:GE15sle',
	GE_GE282_127: 'GE:GE2.82-127',
	Siemens_SWT_23_108: 'Siemens:SWT-2.3-108',
	Vestas_V110_20: 'Vestas:V110-2.0',
	Vestas_V82_165: 'Vestas:V82-1.65',
	Nordex_N149_4_8: 'Nordex:N149-4.8',
	Vestas_V150_40: 'Vestas:V150-40',
	Siemens_SG6_0_170: 'Siemens:SG6.0-170',
} as const
export type PowerCurve = (typeof PowerCurve)[keyof typeof PowerCurve]

const CreateSiteSchema = z.object({
	name: z.string().min(3).max(80),
	description: z.string().max(300).optional(),
	geom: GeometrySchema,
})

const UpdateSiteSchema = CreateSiteSchema.extend({
	vrn: z.string(),
	ownerVrn: z.string(),
})

export const AssetRequestSchema = z.object({
	assetVrn: z.string().optional(),
	siteVrn: z.string().optional(),
	type: z.enum(AssetType, { error: () => i18next.t(($) => $.assets.detailsStep.assetTypeRequiredError) }),
	name: z
		.string()
		.min(3, { error: () => i18next.t(($) => $.assets.detailsStep.assetNameTooShortError) })
		.max(80, { error: () => i18next.t(($) => $.assets.detailsStep.assetNameTooLongError) }),
	description: z.string().max(300, { error: () => i18next.t(($) => $.assets.detailsStep.descriptionTooLongError) }),
	geom: GeometrySchema,
	metadata: z.object({
		hubHeight: z.number().optional(),
		maxMWCapacity: z.number().optional(),
		powerCurve: z.enum(PowerCurve).optional(),
	}),
	// Required for updates when coordinateLabels flag is off ('coordinateLabels' satisfies keyof LDFlagSet)
	site: UpdateSiteSchema.optional(),
	childSites: z
		.array(
			CreateSiteSchema.extend({
				vrn: z.string().optional(),
				name: z.string().max(80, { error: () => i18next.t(($) => $.assets.geometryStep.coordinateLabelTooLongError) }),
			}),
		)
		.optional(),
	deletedChildSitesVrns: z.array(z.string()).optional(),
})

export type AssetRequest = z.infer<typeof AssetRequestSchema>
export const UpdateAssetRequestSchema = AssetRequestSchema.pick({
	assetVrn: true,
	siteVrn: true,
	name: true,
	type: true,
	description: true,
	geom: true,
	metadata: true,
	site: true,
	childSites: true,
	deletedChildSitesVrns: true,
}).partial()

export type UpdateAssetRequest = z.infer<typeof UpdateAssetRequestSchema>
